import { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Spinner } from '@belong/ui';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { PayNowModal } from './PayNow/pay-now-modal';
import type { ResidentAccountSummary } from 'api/models';
import {
  MonthlyStatementDetailInfoModel,
  BillingInfoModel,
  UnderReviewInfoModel,
  GroupedBalancesInfoModel,
  HomeDetailInfoModel,
  MonthSummaryInfoModel,
} from 'api/models.v2';
import {
  format,
  endOfMonth,
  startOfMonth,
  addMonths,
  subMonths,
} from 'date-fns';
import { sortBy } from 'es-toolkit/compat';
import { PastMonthsSection } from 'pages/Accounts/Components/past-months-section';
import { UnderReviewSection } from 'pages/Accounts/Components/under-review-section';
import { UpcomingSection } from 'pages/Accounts/Components/upcoming-section';
import {
  balancesDataMapper,
  underReviewDataMapper,
} from 'pages/Accounts/utils/balances-data-mapper';
import {
  fetchUserGroupBalancesByMonth,
  fetchUserBalancesPastMonths,
  fetchUserBalancesUnderReview,
} from 'store/redux/accounts/actions';
import {
  selectBalancesByMonth,
  selectBalancesPastMonths,
  selectBalancesUnderReview,
} from 'store/redux/accounts/selectors';
import {
  fetchPaymentMethods,
  fetchResidentLeases,
} from 'store/redux/user/actions';
import BalanceDetailsModal from '../../YourPortfolio/Earnings/BalanceDetailsModal/BalanceDetailsModal';
import { PastMonthModal } from '../../YourPortfolio/Earnings/past-month-modal/past-month-modal';
import { getMonthName } from 'pages/Accounts/utils/get-month-name';

type EarningsProps = {
  balances: MonthlyStatementDetailInfoModel[] | null;
  pastMonthsBalances: MonthlyStatementDetailInfoModel[] | null;
  balancesUnderReview: UnderReviewInfoModel[] | null;
  refreshKey?: number;
  shouldDisablePayNow?: boolean;

  fetchUserGroupBalancesByMonth: (queryParams?: string) => void;
  fetchUserBalancesPastMonths: (queryParams?: string) => void;
  fetchResidentLeases: () => Promise<ResidentAccountSummary>;
  fetchUserBalancesUnderReview: (queryParams?: string) => void;
};

type SelectedBalance = {
  balance: GroupedBalancesInfoModel;
  address: string;
  billingInfo?: BillingInfoModel;
};

type PastMonthDetail = HomeDetailInfoModel & {
  monthSummaryInfo: MonthSummaryInfoModel;
};

type AccountsState = Parameters<typeof selectBalancesByMonth>[0];

function stringifyQueryParams(params: Record<string, string>) {
  return new URLSearchParams(params).toString();
}

function Earnings({
  balances,
  pastMonthsBalances,
  balancesUnderReview,
  fetchResidentLeases: fetchResidentLeasesAction,
  fetchUserGroupBalancesByMonth: fetchUserGroupBalancesByMonthAction,
  fetchUserBalancesPastMonths: fetchUserBalancesPastMonthsAction,
  fetchUserBalancesUnderReview: fetchUserBalancesUnderReviewAction,
  refreshKey = 0,
  shouldDisablePayNow = false,
}: EarningsProps) {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [balanceDetailsModal, setBalanceDetailsModal] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<
    SelectedBalance | undefined
  >();
  const [balanceStandalone, setBalanceStandalone] = useState(false);
  const [selectedPastMonth, setSelectedPastMonth] =
    useState<PastMonthDetail | null>(null);
  const [showPayNowModal, setShowPayNowModal] = useState(false);
  const { isEnabled: isPayNowEnabled } = useFeatureFlag('consumer-pay-now');
  const dispatch = useDispatch();

  const fetchData = useCallback(
    async function () {
      setLoading(true);
      const from = startOfMonth(new Date());
      const until = endOfMonth(addMonths(from, 1));

      const balancesQueryParams = stringifyQueryParams({
        from: `${format(from, 'yyyy-MM-dd')}T00:00:00.000Z`,
        until: `${format(until, 'yyyy-MM-dd')}T23:59:59.999Z`,
        groupBalanceType: 'Flat',
      });

      const pastBalancesQueryParams = stringifyQueryParams({
        from: `${format(subMonths(from, 12), 'yyyy-MM-dd')}T00:00:00.000Z`,
        until: `${format(subMonths(until, 2), 'yyyy-MM-dd')}T23:59:59.999Z`,
        groupBalanceType: 'Flat',
      });

      await fetchUserGroupBalancesByMonthAction(balancesQueryParams);
      await fetchUserBalancesPastMonthsAction(pastBalancesQueryParams);
      await fetchUserBalancesUnderReviewAction();
      await dispatch(fetchPaymentMethods());

      const leases = await fetchResidentLeasesAction();

      if (leases.leases?.length) {
        const { address: streetAddress } = leases.leases[0] ?? {};
        setAddress(streetAddress?.streetAddress ?? '');
      }

      setLoading(false);
    },
    [
      fetchUserGroupBalancesByMonthAction,
      fetchUserBalancesPastMonthsAction,
      fetchResidentLeasesAction,
      fetchUserBalancesUnderReviewAction,
      dispatch,
    ],
  );

  useEffect(() => {
    fetchData();
  }, [fetchUserGroupBalancesByMonthAction, fetchData, refreshKey]);

  function handleShowBalanceDetailsModal(
    balance: GroupedBalancesInfoModel,
    streetAddress: string,
    billingInfo?: BillingInfoModel,
    standalone = false,
  ) {
    setBalanceDetailsModal(true);
    setSelectedBalance({ balance, address: streetAddress, billingInfo });
    setBalanceStandalone(standalone);
  }

  function handleBalanceDetailsModalHide() {
    setBalanceDetailsModal(false);
  }

  function handleShowPastMonthModal(detail: PastMonthDetail) {
    setSelectedPastMonth(detail);
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <div>
      <UpcomingSection
        balances={balances ?? []}
        isResident
        selectedHome="all"
        address={address}
        handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
        showPayNow={isPayNowEnabled}
        payNowDisabled={shouldDisablePayNow}
        onPayNowClick={() => setShowPayNowModal(true)}
      />
      <UnderReviewSection
        homesBalances={balancesUnderReview ?? []}
        selectedHome="all"
        handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
        hasManyHomes={false}
        isResident
      />
      <div className="pb-2xl md:pb-0">
        <PastMonthsSection
          address={address}
          balances={pastMonthsBalances ?? []}
          handleShowPastMonthModal={handleShowPastMonthModal}
          isResident={false}
          selectedHome="all"
          hasManyHomes={false}
        />
      </div>
      <BalanceDetailsModal
        show={balanceDetailsModal}
        balance={selectedBalance}
        onHide={handleBalanceDetailsModalHide}
        standalone={balanceStandalone}
        isResident
        showPayNow={isPayNowEnabled}
        onPaymentComplete={(result: {
          success: boolean;
          errorMessage?: string;
        }) => {
          setBalanceDetailsModal(false);
          if (result.success) {
            fetchData();
          }
        }}
      />
      {!!selectedPastMonth && (
        <PastMonthModal
          show
          address={address}
          homeDetail={selectedPastMonth}
          onHide={() => setSelectedPastMonth(null)}
          handleShowBalanceDetailsModal={handleShowBalanceDetailsModal}
          canDownload={false}
        />
      )}
      {isPayNowEnabled && (
        <PayNowModal
          currentMonth={
            balances?.[0]?.monthSummaryInfo?.month
              ? getMonthName(balances[0].monthSummaryInfo.month)
              : ''
          }
          isOpen={showPayNowModal}
          onDismiss={() => {
            setShowPayNowModal(false);
            fetchData();
          }}
          onPaymentComplete={() => {
            setShowPayNowModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

function mapStateProps(state: AccountsState) {
  return {
    balances: balancesDataMapper(
      sortBy(selectBalancesByMonth(state), ['monthSummaryInfo.month']),
    ),
    pastMonthsBalances: balancesDataMapper(selectBalancesPastMonths(state)),
    balancesUnderReview: underReviewDataMapper(
      selectBalancesUnderReview(state),
    ),
  };
}
const mapDispatchToProps = {
  fetchUserGroupBalancesByMonth,
  fetchUserBalancesPastMonths,
  fetchResidentLeases,
  fetchUserBalancesUnderReview,
};

export default connect(mapStateProps, mapDispatchToProps)(Earnings);
